import { menuFor, type MenuItem } from '@/components/admin/menu';
import type { Role } from '@/features/auth/api';

/**
 * パンくず（D-090）。各ページに手書きさせず、URLとロール別メニューから自動で導出する。
 *
 * - 返すのは「親」の列のみ。現在ページの名前は PageHeader の title が持っている
 *   （会員名・イベント名など、メニューには無い具体名を出せるため）
 * - 親メニューは to の前方一致だけでなく子項目でも探す
 *   （例: /directors は「会員管理」(/members) の子。プレフィックスでは辿れない）
 */
export interface Crumb {
  label: string;
  to: string;
}

function matches(to: string, pathname: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/** このメニュー項目（子を含む）が pathname にどれだけ深く一致するか。不一致は -1 */
function depthOf(item: MenuItem, pathname: string): number {
  let depth = item.to !== '/' && matches(item.to, pathname) ? item.to.length : -1;
  for (const child of item.children ?? []) {
    if (matches(child.to, pathname)) depth = Math.max(depth, child.to.length);
  }
  return depth;
}

export function breadcrumbParents(role: Role, pathname: string): Crumb[] {
  // ダッシュボード自身にはパンくずを出さない（親が無い）
  if (pathname === '/') return [];

  const items = menuFor(role);
  const home = items.find((item) => item.to === '/');
  const crumbs: Crumb[] = [{ label: home?.label ?? 'ダッシュボード', to: '/' }];

  let best: MenuItem | undefined;
  let bestDepth = -1;
  for (const item of items) {
    const depth = depthOf(item, pathname);
    if (depth > bestDepth) {
      best = item;
      bestDepth = depth;
    }
  }
  if (best === undefined || bestDepth < 0) return crumbs;

  // 自分自身へのリンクは出さない（現在ページの名前は title 側が出す）
  if (best.to !== pathname) crumbs.push({ label: best.label, to: best.to });

  // 親より深く一致する子があれば挟む（例: /inbox/{id} → メッセージ › 共有受信箱）。
  // 親と同じ to の子（「会員一覧」等）は重複するので出さない
  const child = (best.children ?? [])
    .filter((entry) => entry.to !== best.to && entry.to !== pathname && matches(entry.to, pathname))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (child) crumbs.push({ label: child.label, to: child.to });

  return crumbs;
}
